"use client";

import Link from "next/link";
import { ArrowRight, Mail, MessageSquare, Phone, Users } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { hero as data } from "@/lib/data/contact";

const ctaIconMap = {
  "Request a Demo": ArrowRight,
  "Book a Consultation": Phone,
  "Sales Inquiry": Users,
  "Submit an Inquiry": Mail,
} as const;

export function Hero() {
  return (
    <Section id="top" className="relative overflow-hidden pt-12 pb-16 sm:pt-20 sm:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-[var(--color-cyan)] opacity-30 blur-[140px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-25 blur-[120px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-cyan)]" />
              <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <h1 className="display-hero text-balance">
            {data.title}{" "}
            <span className="bg-gradient-to-br from-[var(--color-cyan)] via-[var(--color-emerald)] to-[var(--color-crimson)] bg-clip-text text-transparent">
              {data.titleAccent}
            </span>
          </h1>
        </Reveal>

        <Reveal delay={160}>
          <p className="mt-6 max-w-3xl text-body leading-relaxed text-[rgb(var(--fg-muted))]">
            {data.lede}
          </p>
        </Reveal>

        <Reveal delay={200}>
          <p className="mt-4 max-w-3xl text-body-sm leading-relaxed text-[rgb(var(--fg-muted))]">
            {data.body}
          </p>
        </Reveal>

        <Reveal delay={240}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => {
              const Icon = ctaIconMap[c.label as keyof typeof ctaIconMap] ?? MessageSquare;
              return (
                <Link
                  key={c.label}
                  href={c.href}
                  className={
                    i === 0
                      ? "btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-cyan)/40]"
                      : "btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                  }
                >
                  {c.label} <Icon className="h-4 w-4" />
                </Link>
              );
            })}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
